import React from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import Layout from '../components/Layout';
import { authService } from '../services/authService';

export default function ChangePasswordPage() {
  const [form, setForm] = React.useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPasswords, setShowPasswords] = React.useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const changePasswordMutation = useMutation({
    mutationFn: (payload: { currentPassword: string; newPassword: string }) =>
      authService.changePassword(payload.currentPassword, payload.newPassword),
    onSuccess: () => {
      window.dispatchEvent(
        new CustomEvent('toast', {
          detail: { type: 'success', message: 'Senha alterada com sucesso!' },
        })
      );
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setErrors({});
    },
    onError: (err: any) => {
      window.dispatchEvent(
        new CustomEvent('toast', {
          detail: { type: 'error', message: err?.message || 'Falha ao alterar senha' },
        })
      );
    },
  });

  const handleChange = (key: string, value: string) => {
    setForm((s) => ({ ...s, [key]: value }));
    if (errors[key]) {
      setErrors((e) => {
        const newErrors = { ...e };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords((s) => ({ ...s, [field]: !s[field] }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!form.currentPassword) {
      newErrors.currentPassword = 'Digite sua senha atual';
    }

    if (!form.newPassword) {
      newErrors.newPassword = 'Digite uma nova senha';
    } else if (form.newPassword.length < 6) {
      newErrors.newPassword = 'A senha deve ter no mínimo 6 caracteres';
    } else if (!/[A-Z]/.test(form.newPassword)) {
      newErrors.newPassword = 'A senha deve incluir letras maiúsculas';
    } else if (!/[a-z]/.test(form.newPassword)) {
      newErrors.newPassword = 'A senha deve incluir letras minúsculas';
    } else if (!/[0-9]/.test(form.newPassword)) {
      newErrors.newPassword = 'A senha deve incluir números';
    } else if (!/[!@#$%^&*]/.test(form.newPassword)) {
      newErrors.newPassword = 'A senha deve incluir caracteres especiais (!@#$%^&*)';
    }

    if (!form.confirmPassword) {
      newErrors.confirmPassword = 'Confirme sua nova senha';
    } else if (form.newPassword !== form.confirmPassword) {
      newErrors.confirmPassword = 'As senhas não conferem';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    await changePasswordMutation.mutateAsync({
      currentPassword: form.currentPassword,
      newPassword: form.newPassword,
    });
  };

  return (
    <Layout>
      <div className="flex flex-col h-full bg-gray-100">
        <div className="bg-white border-b border-gray-200 px-4 md:px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center">
              <Lock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Alterar Senha</h1>
              <p className="text-gray-600 text-sm mt-1">Mantenha sua conta segura atualizando sua senha regularmente</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-lg shadow p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                  <Lock className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Segurança da Conta</h2>
                  <p className="text-sm text-gray-600">Sua senha deve ter no mínimo 6 caracteres</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-semibold text-blue-900 mb-2">Dica de Segurança:</p>
                <p className="text-sm text-blue-800">
                  Use uma senha forte com letras maiúsculas, minúsculas, números e caracteres especiais.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">Senha Atual</label>
                  <div className="relative">
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      value={form.currentPassword}
                      onChange={(e) => handleChange('currentPassword', e.target.value)}
                      placeholder="Digite sua senha atual"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 pr-10 ${
                        errors.currentPassword ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('current')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.currentPassword && <p className="text-xs text-red-600 mt-1">{errors.currentPassword}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">Nova Senha</label>
                  <div className="relative">
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      value={form.newPassword}
                      onChange={(e) => handleChange('newPassword', e.target.value)}
                      placeholder="Digite sua nova senha"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 pr-10 ${
                        errors.newPassword ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('new')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.newPassword && <p className="text-xs text-red-600 mt-1">{errors.newPassword}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">Confirmar Nova Senha</label>
                  <div className="relative">
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={form.confirmPassword}
                      onChange={(e) => handleChange('confirmPassword', e.target.value)}
                      placeholder="Digite sua nova senha novamente"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 pr-10 ${
                        errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('confirm')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="text-xs text-red-600 mt-1">{errors.confirmPassword}</p>}
                </div>

                <button
                  type="submit"
                  disabled={changePasswordMutation.isPending}
                  className="w-full bg-gray-900 text-white py-2 rounded-md hover:bg-gray-800 font-medium disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {changePasswordMutation.isPending ? 'Alterando...' : 'Alterar Senha'}
                </button>
              </form>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg shadow p-4 mt-6">
              <div className="flex gap-3">
                <Lock className="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-yellow-900 text-sm">Esqueceu sua senha?</h3>
                  <p className="text-sm text-yellow-800 mt-1">
                    Se você esqueceu sua senha atual, faça logout e use a opção "Esqueci minha senha" na tela de login.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
